import {
  getTelegramWebApp,
  isTelegramVersionAtLeast,
  TelegramWebAppUtils,
} from "../../shared/api/telegram";
import { ensurePrefixedStorageKey } from "../../shared/config/storage-keys";

export interface StorageDriver {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<boolean>;
  removeItem(key: string): Promise<boolean>;
}

type LegacyStorageEnvelopeVersion = {
  time: number;
  sequence: number;
  writerId: string;
};

type LegacyStorageEnvelope = {
  __tonesimReplicatedStorage: 1;
  version: LegacyStorageEnvelopeVersion;
  value: string | null;
};

type LegacyStorageCandidate = {
  driver: StorageDriver;
  envelope: LegacyStorageEnvelope;
};

const isLegacyStorageEnvelopeVersion = (
  value: unknown,
): value is LegacyStorageEnvelopeVersion => {
  if (typeof value !== 'object' || value === null) return false;
  const version = value as Partial<LegacyStorageEnvelopeVersion>;
  return (
    typeof version.time === 'number' &&
    Number.isSafeInteger(version.time) &&
    version.time >= 0 &&
    typeof version.sequence === 'number' &&
    Number.isSafeInteger(version.sequence) &&
    version.sequence >= 0 &&
    typeof version.writerId === 'string'
  );
};

const decodeLegacyStorageEnvelope = (
  value: string,
): LegacyStorageEnvelope | undefined => {
  try {
    const envelope = JSON.parse(value) as Partial<LegacyStorageEnvelope>;
    if (
      envelope.__tonesimReplicatedStorage === 1 &&
      isLegacyStorageEnvelopeVersion(envelope.version) &&
      (typeof envelope.value === 'string' || envelope.value === null)
    ) {
      return envelope as LegacyStorageEnvelope;
    }
  } catch {
    // Non-envelope values stay untouched.
  }

  return undefined;
};

const compareLegacyStorageVersions = (
  left: LegacyStorageEnvelopeVersion,
  right: LegacyStorageEnvelopeVersion,
): number =>
  left.time - right.time ||
  left.sequence - right.sequence ||
  left.writerId.localeCompare(right.writerId);

const createLegacyNeutralizationEnvelope = (
  version: LegacyStorageEnvelopeVersion,
): string | undefined => {
  const neutralizationVersion = version.time < Number.MAX_SAFE_INTEGER
    ? { time: version.time + 1, sequence: 0, writerId: 'legacy-migration' }
    : version.sequence < Number.MAX_SAFE_INTEGER
      ? {
          time: version.time,
          sequence: version.sequence + 1,
          writerId: 'legacy-migration',
        }
      : undefined;
  if (!neutralizationVersion) return undefined;

  return JSON.stringify({
    __tonesimReplicatedStorage: 1,
    version: neutralizationVersion,
    value: null,
  } satisfies LegacyStorageEnvelope);
};

export class TelegramStorageDriver implements StorageDriver {
  constructor(
    private readonly name: "secure" | "device", // cloud intentionally skipped
  ) {}

  private get driver() {
    return TelegramWebAppUtils.storage[this.name];
  }

  isAvailable(): boolean {
    const telegram = getTelegramWebApp();
    return Boolean(
      telegram &&
      isTelegramVersionAtLeast(telegram.version, '9.0') &&
      (this.name === "secure"
        ? telegram.SecureStorage
        : telegram.DeviceStorage),
    );
  }

  async getItem(key: string): Promise<string | null> {
    if (!this.isAvailable()) return null;
    try {
      return await this.driver.getItem(key);
    } catch {
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<boolean> {
    try {
      return Boolean(await this.driver.setItem(key, value));
    } catch {
      return false;
    }
  }

  async removeItem(key: string): Promise<boolean> {
    try {
      return Boolean(await this.driver.removeItem(key));
    } catch {
      return false;
    }
  }
}

export class LocalStorageDriver implements StorageDriver {
  isAvailable(): boolean {
    if (typeof window === "undefined") return false;
    try {
      return Boolean(window.localStorage);
    } catch {
      return false;
    }
  }

  async getItem(key: string): Promise<string | null> {
    if (typeof window === "undefined") return null;
    try {
      const storage = window.localStorage;
      if (!storage) return null;
      const storageKey = process.env.NODE_ENV === "production" ? ensurePrefixedStorageKey(key) : key;
      return storage.getItem(storageKey);
    } catch {
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<boolean> {
    if (typeof window === "undefined") return false;
    try {
      const storage = window.localStorage;
      if (!storage) return false;
      const storageKey = process.env.NODE_ENV === "production" ? ensurePrefixedStorageKey(key) : key;
      storage.setItem(storageKey, value);
      return true;
    } catch {
      return false;
    }
  }

  async removeItem(key: string): Promise<boolean> {
    if (typeof window === "undefined") return false;
    try {
      const storage = window.localStorage;
      if (!storage) return false;
      const storageKey = process.env.NODE_ENV === "production" ? ensurePrefixedStorageKey(key) : key;
      storage.removeItem(storageKey);
      return true;
    } catch {
      return false;
    }
  }
}

export class LayeredStorage implements StorageDriver {
  constructor(private readonly drivers: StorageDriver[]) {}

  async getItem(key: string): Promise<string | null> {
    const legacyCandidates: LegacyStorageCandidate[] = [];

    for (const driver of this.drivers) {
      let value: string | null;
      try {
        value = await driver.getItem(key);
      } catch {
        continue;
      }
      if (value === null || value === undefined) continue;

      const envelope = decodeLegacyStorageEnvelope(value);
      if (envelope === undefined) {
        if (legacyCandidates.length === 0) return value;
        continue;
      }

      legacyCandidates.push({ driver, envelope });
    }

    if (legacyCandidates.length === 0) return null;

    const newestCandidate = legacyCandidates.reduce(
      (newest, candidate) =>
        compareLegacyStorageVersions(
          candidate.envelope.version,
          newest.envelope.version,
        ) > 0
          ? candidate
          : newest,
    );
    await this.reconcileLegacyCandidates(
      key,
      legacyCandidates,
      newestCandidate,
    );
    return newestCandidate.envelope.value;
  }

  private async reconcileLegacyCandidates(
    key: string,
    candidates: LegacyStorageCandidate[],
    newestCandidate: LegacyStorageCandidate,
  ): Promise<void> {
    const neutralizationEnvelope = createLegacyNeutralizationEnvelope(
      newestCandidate.envelope.version,
    );

    for (const candidate of candidates) {
      if (candidate === newestCandidate || candidate.envelope.value === null) {
        continue;
      }

      if (!neutralizationEnvelope || !await this.neutralizeLegacyCandidate(
        candidate.driver,
        key,
        neutralizationEnvelope,
      )) {
        throw new Error('Unable to safely reconcile legacy storage');
      }
    }

    const value = newestCandidate.envelope.value;
    if (value === null) return;
    if (await this.writeLegacyValue(newestCandidate.driver, key, value)) {
      return;
    }

    throw new Error('Unable to safely reconcile legacy storage');
  }

  private async writeLegacyValue(
    driver: StorageDriver,
    key: string,
    value: string,
  ): Promise<boolean> {
    try {
      return await driver.setItem(key, value);
    } catch {
      return false;
    }
  }

  private async neutralizeLegacyCandidate(
    driver: StorageDriver,
    key: string,
    tombstone: string,
  ): Promise<boolean> {
    try {
      if (await driver.removeItem(key)) return true;
    } catch {
      // Try an authoritative tombstone when cleanup throws.
    }

    return this.writeLegacyValue(driver, key, tombstone);
  }

  async setItem(key: string, value: string): Promise<boolean> {
    for (const driver of this.drivers) {
      const success = await driver.setItem(key, value);
      if (success) {
        return true;
      }
    }
    return false;
  }

  async removeItem(key: string): Promise<boolean> {
    let removed = false;
    for (const driver of this.drivers) {
      const success = await driver.removeItem(key);
      removed = success || removed;
    }
    return removed;
  }
}

const createTelegramStorageDrivers = (): StorageDriver[] => [
  new TelegramStorageDriver("secure"),
  new TelegramStorageDriver("device"),
  new LocalStorageDriver(),
];

export const createDefaultStorage = () =>
  new LayeredStorage(createTelegramStorageDrivers());
